// The model showcase (spec-v4-models.md section 2, V4-MS): every video model on the same 12 style prompts, from
// GET /api/model-showcase (public). Pure helpers (parse, filter, pick) plus the loader; the Models screen, the landing
// and the stage's side-by-side component all read through here. Nothing shows until at least one clip is made.

export type ShowcaseSpeed = "fast" | "standard";
export type ShowcaseStyleInfo = { id: string; n: number; name: string; watch: string; tests: string[]; tags: string[] };
export type ShowcaseModelInfo = {
  id: string;
  label: string;
  short: string;
  provider: string;
  creditsPer5s: number;
  durationS: number;
  hasAudio: boolean;
  /** from the model's name until clips are timed; see speedOf */
  speed: ShowcaseSpeed;
  rateSource: "documented" | "unverified";
  /** the median time the showcase's clips took to make, in seconds */
  madeSeconds: number | null;
};
export type ShowcaseCell = { styleId: string; modelId: string; videoUrl: string; posterUrl: string; durationS: number; seconds: number | null; madeAt: string | null };
export type ShowcaseData = { note: string; styles: ShowcaseStyleInfo[]; models: ShowcaseModelInfo[]; cells: ShowcaseCell[]; updatedAt: string | null };

export const EMPTY_SHOWCASE: ShowcaseData = { note: "", styles: [], models: [], cells: [], updatedAt: null };

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
/** Only same-origin paths, https links, or (mock) data: images: never a javascript: URL in a src. */
const safeUrl = (v: unknown, allowData = false): string => {
  const s = str(v);
  if (/^\/(?!\/)/.test(s) || /^https:\/\//i.test(s)) return s;
  if (allowData && /^data:image\/(svg\+xml|png|jpeg|webp)[;,]/i.test(s)) return s;
  return "";
};

/** Reads the api's answer defensively: a bad row is dropped, never the page. */
export function parseShowcase(raw: unknown, opts: { allowDataUrls?: boolean } = {}): ShowcaseData {
  if (!isObj(raw)) return EMPTY_SHOWCASE;
  const styles = (Array.isArray(raw.styles) ? raw.styles : []).filter(isObj).map((s, i) => ({
    id: str(s.id), n: num(s.n) ?? i + 1, name: str(s.name), watch: str(s.watch),
    tests: Array.isArray(s.tests) ? s.tests.filter((t): t is string => typeof t === "string") : [],
    tags: Array.isArray(s.tags) ? s.tags.filter((t): t is string => typeof t === "string") : [],
  })).filter((s) => s.id && s.name);
  const models = (Array.isArray(raw.models) ? raw.models : []).filter(isObj).map((m) => ({
    id: str(m.id), label: str(m.label), short: str(m.short) || str(m.label), provider: str(m.provider),
    creditsPer5s: num(m.creditsPer5s) ?? 0, durationS: num(m.durationS) ?? 5, hasAudio: m.hasAudio === true,
    speed: (m.speed === "fast" ? "fast" : "standard") as ShowcaseSpeed, rateSource: (m.rateSource === "unverified" ? "unverified" : "documented") as ShowcaseModelInfo["rateSource"],
    madeSeconds: num(m.madeSeconds),
  })).filter((m) => m.id && m.label);
  const styleIds = new Set(styles.map((s) => s.id));
  const modelIds = new Set(models.map((m) => m.id));
  const cells = (Array.isArray(raw.cells) ? raw.cells : []).filter(isObj).map((c) => ({
    styleId: str(c.styleId), modelId: str(c.modelId), videoUrl: safeUrl(c.videoUrl), posterUrl: safeUrl(c.posterUrl, opts.allowDataUrls),
    durationS: num(c.durationS) ?? 5, seconds: num(c.seconds), madeAt: str(c.madeAt) || null,
  })).filter((c) => styleIds.has(c.styleId) && modelIds.has(c.modelId) && c.videoUrl);
  return { note: str(raw.note), styles, models, cells, updatedAt: str(raw.updatedAt) || null };
}

export const hasShowcase = (d: ShowcaseData | null | undefined): d is ShowcaseData => !!d && d.cells.length > 0;

/** Made within this many seconds counts as fast once the showcase has timed it (most fast models take under 1.5 min). */
export const FAST_SECONDS = 90;
export function speedOf(m: Pick<ShowcaseModelInfo, "speed" | "madeSeconds">): ShowcaseSpeed {
  return m.madeSeconds !== null ? (m.madeSeconds <= FAST_SECONDS ? "fast" : "standard") : m.speed;
}

export type PriceFilter = "any" | "100" | "200";
export const PRICE_FILTERS: { id: PriceFilter; label: string; max: number | null }[] = [
  { id: "any", label: "Any price", max: null },
  { id: "100", label: "Up to 100", max: 100 },
  { id: "200", label: "Up to 200", max: 200 },
];
export type ShowcaseFilters = { price: PriceFilter; fast: boolean; audio: boolean };
export const NO_FILTERS: ShowcaseFilters = { price: "any", fast: false, audio: false };

/** The models that pass the filters, in the api's (catalog) order. */
export function filterModels(models: ShowcaseModelInfo[], f: ShowcaseFilters): ShowcaseModelInfo[] {
  const max = PRICE_FILTERS.find((p) => p.id === f.price)?.max ?? null;
  return models.filter((m) => (max === null || m.creditsPer5s <= max) && (!f.fast || speedOf(m) === "fast") && (!f.audio || m.hasAudio));
}

export const cellOf = (d: ShowcaseData, styleId: string, modelId: string) => d.cells.find((c) => c.styleId === styleId && c.modelId === modelId) ?? null;

/** The style to open on: the asked one when it has clips, else the first that has. */
export function pickStyle(d: ShowcaseData, asked?: string | null): ShowcaseStyleInfo | null {
  return d.styles.find((s) => s.id === asked && d.cells.some((c) => c.styleId === s.id)) ?? d.styles.find((s) => d.cells.some((c) => c.styleId === s.id)) ?? null;
}

/** A model id the user or the Director named: the exact id, else its words ("kling 2.5", "veo"). */
export function findShowcaseModel(models: ShowcaseModelInfo[], q: string): ShowcaseModelInfo | null {
  const s = q.trim().toLowerCase();
  if (!s) return null;
  return models.find((m) => m.id.toLowerCase() === s) ?? models.find((m) => m.short.toLowerCase() === s) ?? models.find((m) => m.label.toLowerCase().includes(s) || m.short.toLowerCase().includes(s)) ?? null;
}

export type CompareClip = { key: string; title: string; detail: string; videoUrl: string; posterUrl: string; hasAudio: boolean };

/** 2-4 clips of one style side by side (the stage, the Director's "here's Kling vs Seedance"); unknown or unmade ones drop out. */
export function compareClips(d: ShowcaseData, styleId: string, modelIds: string[]): CompareClip[] {
  const out: CompareClip[] = [];
  for (const id of modelIds) {
    const m = findShowcaseModel(d.models, id);
    const c = m && cellOf(d, styleId, m.id);
    if (!m || !c || out.some((x) => x.key === m.id)) continue;
    out.push({ key: m.id, title: m.short, detail: `${m.creditsPer5s} credits per 5 s`, videoUrl: c.videoUrl, posterUrl: c.posterUrl, hasAudio: m.hasAudio });
    if (out.length === 4) break;
  }
  return out;
}

/** "Made in 48 s", "Made in 2 min" */
export function madeWords(seconds: number | null): string | null {
  if (seconds === null) return null;
  return seconds < 90 ? `Made in ${Math.round(seconds)} s` : `Made in ${Math.round(seconds / 60)} min`;
}

/** GET /api/model-showcase (or the mock's data under VITE_MOCK=1). Null when it can't be read: callers show nothing. */
export async function loadModelShowcase(fetcher: typeof fetch = fetch, timeoutMs = 6000): Promise<ShowcaseData | null> {
  if (import.meta.env?.VITE_MOCK === "1") return (await import("./mock/showcase")).mockShowcase();
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const t = setTimeout(() => ctl?.abort(), timeoutMs);
  try {
    const r = await fetcher("/api/model-showcase", { credentials: "omit", headers: { accept: "application/json" }, signal: ctl?.signal });
    return r.ok ? parseShowcase(await r.json()) : null;
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}
